import { Star } from 'lucide-react';

import { avatarClasses, avatarInitials } from './collaboratorAvatar';

/**
 * Forme minimale d'un membre pour la pile d'avatars — structurellement compatible
 * avec PersonneMoraleCollaboratorPreviewDto (liste des personnes morales) et avec le
 * champ `membres` des lignes cabinet des écrans de rôles.
 */
export interface MemberAvatarPreview {
  accessId: string;
  abpUserId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  isPrincipal?: boolean;
  isActive?: boolean;
}

interface Props {
  members: MemberAvatarPreview[];
  totalCount: number;
  maxVisible?: number;
  /** false = pile d'avatars seule, sans le nom du gérant principal dessous. */
  showPrincipalName?: boolean;
}

const initials = (m: MemberAvatarPreview): string =>
  avatarInitials(m.firstName, m.lastName, m.email);

const fullName = (m: MemberAvatarPreview): string => {
  const name = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
  return name || m.email || 'Sans nom';
};

export const CollaboratorsAvatarStack: React.FC<Props> = ({
  members,
  totalCount,
  maxVisible = 3,
  showPrincipalName = true,
}) => {
  const principal = members.find((m) => m.isPrincipal) ?? members[0];
  const visible = members.slice(0, maxVisible);
  const overflow = Math.max(0, totalCount - visible.length);

  if (members.length === 0) {
    return <span className="text-xs text-gray-400">Aucun collaborateur</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center -space-x-1.5">
        {/* Keyé par compte : les aperçus « hors cabinet » (repli legacy des
            écrans de rôles) n'ont pas d'accès — leur accessId est vide et
            dupliqué, l'abpUserId est unique dans tous les cas. */}
        {visible.map((m) => (
          <div
            key={m.abpUserId}
            title={`${fullName(m)}${m.isPrincipal ? ' (Principal)' : ''}`}
            className={`relative w-6 h-6 rounded-full ring-2 ring-white flex items-center justify-center text-[9px] font-bold ${avatarClasses(
              m.abpUserId,
            )} ${!m.isActive ? 'opacity-50' : ''}`}
          >
            <span>{initials(m)}</span>
            {m.isPrincipal && (
              <Star
                size={10}
                className="absolute -top-1 -left-1 fill-amber-400 text-amber-400 drop-shadow-sm"
              />
            )}
          </div>
        ))}
        {overflow > 0 && (
          <div
            title={`+${overflow} autre${overflow > 1 ? 's' : ''} collaborateur${overflow > 1 ? 's' : ''}`}
            className="relative w-6 h-6 rounded-full ring-2 ring-white bg-gray-200 text-gray-700 flex items-center justify-center text-[9px] font-semibold"
          >
            +{overflow}
          </div>
        )}
      </div>
      {showPrincipalName && principal && (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
            {fullName(principal)}
          </span>
          <span className="text-[10px] text-amber-700 uppercase tracking-wide font-semibold">
            ★ Gérant principal
          </span>
        </div>
      )}
    </div>
  );
};
